import { takeUntil, tap } from 'rxjs/operators';
import { Component, EventEmitter, Input, Output, input, model } from '@angular/core';

import { ComoService } from '@core/services/como/como.service';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { AnonymousAuthenticationService } from '@core/services/auth/anonymous-authentification.service';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { getAllCountries } from '@shared/components/input-country-phone/config/countries.config';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { MarketplaceService } from '@core/services/marketplace/marketplace.service';
import { ComoAttachUserResponse } from '@core/models/como.model';
import { routeComoRegistrationAfterAttach } from '@shared/modals/como/como-registration-modal/como-registration-route.helper';

@Component({
  selector: 'app-phone-number-step',
  templateUrl: './phone-number-step.component.html',
  standalone: false,
})
export class PhoneNumberStepComponent extends DestroyerBase {
  readonly phone = model<string>('');
  readonly useLocalFormat = input<boolean>(false);
  readonly defaultRegionCode = input<string>('fr');

  @Output() onClose = new EventEmitter<Event>();
  @Output() goToNextStep = new EventEmitter<void>();
  @Output() goToAuthenticationStep = new EventEmitter<void>();
  @Output() goToComplete = new EventEmitter<void>();
  @Output() goToAccountNotFoundStep = new EventEmitter<void>();
  @Output() goToKioskAccountNotFoundStep = new EventEmitter<void>();

  @Input() accentColor?: ContentBuilderFieldColors;

  isValidating = false;
  errorMessage?: string;

  kioskParameters = this.settingsSelector.kiosk;

  constructor(
    private readonly comoService: ComoService,
    private readonly anonymousAuthenticationService: AnonymousAuthenticationService,
    private readonly settingsSelector: SettingsStoreSelector,
    private readonly analyticsService: AnalyticsService,
    private readonly authStoreSelector: AuthStoreSelector,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly marketplaceService: MarketplaceService,
  ) {
    super();
  }

  onRegister(): void {
    const phone = this.phone();
    if (!phone) {
      return;
    }
    this.analyticsService.trackLoyaltyButtonClick('como', 'submit_phone');
    this.isValidating = true;
    this.errorMessage = undefined;
    this.comoService
      .linkComoUser$({ phoneOrEmail: phone })
      .pipe(
        takeUntil(this._destroyerRef),
        tap(response => this.afterPhoneLink(response)),
      )
      .subscribe({
        error: error => this.handleErrorResponse(error.error, this.phone()),
        complete: () => (this.isValidating = false),
      });
  }

  anonymousLogin(): void {
    this.anonymousAuthenticationService.createAnonymousSession$().subscribe();
  }

  onKeypadNumberClick(number: number): void {
    const currentValue = this.phone();
    this.phone.set(currentValue + number.toString());
  }

  onKeypadDeleteClick(): void {
    const currentValue = this.phone();
    const defaultRegionCode = this.defaultRegionCode() || 'fr';

    // Get the default dial code based on the region
    const defaultDialCode = this.getDefaultDialCode(defaultRegionCode);
    // Don't allow deletion if it would remove part of the default dial code
    // or if the current value is already just the dial code
    if (currentValue.length <= defaultDialCode.length || currentValue === defaultDialCode) {
      return;
    }

    this.phone.set(currentValue.slice(0, -1));
  }

  private getDefaultDialCode(regionCode: string): string {
    // Get all countries and create a map from iso2 to dial code
    const countries = getAllCountries();
    const country = countries.find(c => c.iso2.toLowerCase() === regionCode.toLowerCase());

    if (country) {
      return `+${country.dialCode}`;
    }

    // Default to France if region code not found
    return '+33';
  }

  private afterPhoneLink(response: ComoAttachUserResponse): void {
    if (response.otp_sent) {
      this.analyticsService.log({
        level: 'info',
        label: '[DOOD-3204] ComoPhoneNumberStep.linkResponse',
        message: {
          next_step: 'otp',
          otp_sent: true,
          has_access_token: !!response.access_token,
          has_custom_token: !!response.custom_token,
          result: response.result ?? null,
        },
      });
      this.goToNextStep.next();
      return;
    }

    const nextStep = routeComoRegistrationAfterAttach(response, {
      comoService: this.comoService,
      authStoreSelector: this.authStoreSelector,
      marketplaceSelector: this.marketplaceSelector,
      marketplaceService: this.marketplaceService,
      analyticsService: this.analyticsService,
      source: 'phone',
    });

    this.analyticsService.log({
      level: nextStep === 'complete' ? 'info' : 'warn',
      label: '[DOOD-3204] ComoPhoneNumberStep.linkResponse',
      message: {
        next_step: nextStep,
        otp_sent: false,
        has_access_token: !!response.access_token,
        has_custom_token: !!response.custom_token,
        result: response.result ?? null,
      },
    });

    if (nextStep === 'complete') {
      this.goToComplete.next();
      return;
    }
    this.goToAuthenticationStep.next();
  }

  private handleErrorResponse(error: any, _phone: string): void {
    if (error.code === 202) {
      this.analyticsService.trackErrorDisplay(
        'account_not_found',
        error.code?.toString(),
        'como.link-account.errors.account-not-found-phone',
        'como_phone_step',
      );
      if (!this.kioskParameters.isKioskLayout) {
        this.goToAccountNotFoundStep.next();
      } else {
        this.goToKioskAccountNotFoundStep.next();
      }
    }
    this.errorMessage = this.comoService.getErrorMessageFromCode(error.code);
    if (this.errorMessage && error.code !== 202) {
      this.analyticsService.trackErrorDisplay(
        'loyalty_error',
        error.code?.toString(),
        this.errorMessage,
        'como_phone_step',
      );
    }
  }

  onUpdatePhone(phone: string) {
    this.phone.set(phone);
  }

  public handleKeyDown(event: KeyboardEvent): void {
    // Prevent default action for Enter key to avoid triggering the first button
    if (event.key === 'Enter') {
      event.preventDefault();
      // Optionally trigger form submission if the form is valid
      if (this.phone()) {
        this.onRegister();
      }
    }
  }
}
